export interface KnowledgeSyncButtonLabels {
  idle: string;
  processing: string;
  ready: string;
  stale: string;
  failed: string;
}

export interface KnowledgeSyncButtonConfig {
  refType: string;
  refID: string;
  status: string;
  lastSyncAt: string;
  isStale: boolean;
  labels: KnowledgeSyncButtonLabels;
}

/**
 * Alpine component factory for a generic knowledge-base sync button.
 *
 * Listens for `knowledge_sync_status` SSE events keyed on refType + refID.
 * Button is enabled when:
 *   - Never published (status === '')
 *   - Failed
 *   - Stale (content changed since last publish)
 * Button is disabled (greyed) when ready and not stale, or while processing.
 */
export function knowledgeSyncButton(config: KnowledgeSyncButtonConfig) {
  return {
    status: config.status,
    lastSyncAt: config.lastSyncAt,
    isStale: config.isStale,
    _labels: config.labels,

    get isProcessing(): boolean {
      return (
        this.status === "processing" || this.status === "enriching" || this.status === "embedding"
      );
    },

    get isReady(): boolean {
      return this.status === "ready";
    },

    get isFailed(): boolean {
      return this.status === "failed";
    },

    get tooltipLabel(): string {
      if (this.isProcessing) return this._labels.processing;
      if (this.isReady && this.isStale) return this._labels.stale;
      if (this.isReady) return this._labels.ready;
      if (this.isFailed) return this._labels.failed;
      return this._labels.idle;
    },

    /** Called by @htmx:after-request on the button to handle the sync response. */
    onSyncResponse(event: Event) {
      const htmxEvent = event as CustomEvent;
      if (!htmxEvent.detail.successful) return;
      // Optimistically mark as processing; SSE will update to final state.
      this.status = "processing";
    },

    init() {
      window.addEventListener("knowledge_sync_status", (e: Event) => {
        const ce = e as CustomEvent;
        try {
          const data = JSON.parse(ce.detail.data);
          if (data.ref_type === config.refType && data.ref_id === config.refID) {
            this.status = data.status;
            if (data.last_sync_at) {
              this.lastSyncAt = data.last_sync_at;
            }
            // If we just became ready, clear stale flag.
            if (data.status === "ready") {
              this.isStale = false;
            }
          }
        } catch {
          // Ignore parse errors
        }
      });
    },
  };
}
